import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type PushSubscriptionKind = 'WEB' | 'DESKTOP';

export type PushSubscriptionPayload = {
  endpoint: string;
  p256dh?: string;
  auth?: string;
  kind?: PushSubscriptionKind;
};

export const PushNotificationType = {
  COMMENT: 'comment',
  COMPLETION_REPORT: 'completion-report',
  POST_MORTEM: 'post-mortem',
  CO_ACTION_CREATED: 'co-action-created',
  CODE_REVIEW_COMPLETED: 'code-review-completed',
  DAEMON_TRIGGER_STATUS_CHANGED: 'daemon-trigger-status-changed',
  CONVENTION_CREATED: 'convention-created',
  CONVENTION_UPDATED: 'convention-updated',
  CONVENTION_DELETED: 'convention-deleted',
  SKILL_CREATED: 'skill-created',
  SKILL_UPDATED: 'skill-updated',
  SKILL_DELETED: 'skill-deleted',
  DOCUMENT_CREATED: 'document-created',
  DOCUMENT_UPDATED: 'document-updated',
  DOCUMENT_DELETED: 'document-deleted',
  DOCUMENT_COMMENT: 'document-comment',
  PLAN_CREATED: 'plan-created',
  PLAN_STATUS_CHANGED: 'plan-status-changed',
  RUNNER_UPDATE_COMPLETED: 'runner-update-completed',
  RUNNER_UPDATE_APPLIED: 'runner-update-applied',
  RUNNER_UPDATE_FAILED: 'runner-update-failed',
} as const;

export type PushNotificationType = (typeof PushNotificationType)[keyof typeof PushNotificationType];

export type NotificationPreference = {
  type: PushNotificationType;
  enabled: boolean;
};

type PushSubscriptionResponse = {
  data: {
    id: string;
    memberId: string;
    projectId: string;
    endpoint: string;
    createdAt: string;
    updatedAt: string;
  };
};

type PushSubscriptionStatusResponse = {
  data: {
    isSubscribed: boolean;
  };
};

type ProjectsSubscriptionStatusResponse = {
  data: {
    projectId: string;
    isSubscribed: boolean;
  }[];
};

type NotificationPreferencesResponse = {
  data: NotificationPreference[];
};

export const subscribePushNotification = async (
  token: string,
  projectId: string,
  payload: PushSubscriptionPayload,
): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/push/subscribe`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to subscribe push notification: ${response.status}`);
  }

  (await response.json()) as PushSubscriptionResponse;
};

export const unsubscribePushNotification = async (
  token: string,
  projectId: string,
  endpoint: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/push/subscribe`, {
    method: 'DELETE',
    headers: authHeaders(token, true),
    body: JSON.stringify({ endpoint }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unsubscribe push notification: ${response.status}`);
  }
};

export const getPushSubscriptionStatus = async (
  token: string,
  projectId: string,
  endpoint: string,
): Promise<boolean> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`,
    {
      method: 'GET',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to get push subscription status: ${response.status}`);
  }

  const data = (await response.json()) as PushSubscriptionStatusResponse;
  return data.data.isSubscribed;
};

export const getProjectsSubscriptionStatus = async (
  token: string,
  endpoint: string,
): Promise<{ projectId: string; isSubscribed: boolean }[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/push/subscriptions?endpoint=${encodeURIComponent(endpoint)}`,
    {
      method: 'GET',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch push subscriptions: ${response.status}`);
  }

  const data = (await response.json()) as ProjectsSubscriptionStatusResponse;
  return data.data;
};

export const getNotificationPreferences = async (
  token: string,
  projectId: string,
): Promise<NotificationPreference[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/push/preferences`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch notification preferences: ${response.status}`);
  }

  const data = (await response.json()) as NotificationPreferencesResponse;
  return data.data;
};

export const updateNotificationPreferences = async (
  token: string,
  projectId: string,
  items: NotificationPreference[],
): Promise<NotificationPreference[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/push/preferences`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(items),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update notification preferences: ${response.status}`);
  }

  const data = (await response.json()) as NotificationPreferencesResponse;
  return data.data;
};

export const sendTestPushNotification = async (
  token: string,
  projectId: string,
  endpoint?: string | null,
): Promise<{ delivered: number }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/push/test`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(endpoint ? { endpoint } : {}),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to send test notification: ${response.status}`);
  }

  const data = (await response.json()) as { data: { delivered: number } };
  return data.data;
};
